import { updateQueryStringParameters } from 'common/js/helpers';
import { action, makeAutoObservable, when } from 'mobx';
import joinUrl from 'url-join';
import globalConfig from '../js/config';
import { UITimerStore } from './UITimerStore';
import { generateUrlWithRegion } from '../js/url-utils';
import ajaxPromise from 'common/js/AjaxPromises';
import {
  sendMraidDebugTracks,
  setClickPositionInTrack
} from 'common/js/TrackAnalyticsHelper';
import { DebugLog } from '../js/debug-log';
import { getOgAdContext, isWeb } from 'common/js/environment-utils';
import { getScreenSize } from 'common/js/screen-utils';
import { isSupplyMonitoringEventsEnabled } from 'common/js/supply-monitoring-events-enabling';

interface AnalyticsStoreProps {
  uiTimerStore: UITimerStore;
  adContext: AdContext;
  formatConfig: any;
}

export class AnalyticsStore {
  private static _instance: AnalyticsStore;

  uiTimerStore: UITimerStore;
  adContext: AdContext;
  formatConfig: any;
  private isImpressionFormatSent: boolean = false;
  private pendingTracks: number = 0;
  private isFormatAvailableInterval: NodeJS.Timeout | undefined;
  private shouldTrackFormatAvailability: boolean = false;

  private trackWaitPromise: Promise<boolean> | null = null;

  static get instance(): AnalyticsStore {
    return this._instance;
  }

  static initStore(analyticsStoreProps: AnalyticsStoreProps): AnalyticsStore {
    if (this.instance) return this.instance;

    return (this._instance = new AnalyticsStore(analyticsStoreProps));
  }

  private constructor({
    uiTimerStore,
    adContext,
    formatConfig
  }: AnalyticsStoreProps) {
    this.uiTimerStore = uiTimerStore;
    this.adContext = adContext;
    this.formatConfig = formatConfig;

    makeAutoObservable(this);
  }

  commonTrackData() {
    return {
      a_sdk: window.MRAID_ENV?.sdkVersion || undefined,
      ak: this.adContext.apiKey,
      auid: this.adContext.adUnitId,
      imp: this.adContext.impressionId,
      oc_id: parseInt(this.adContext.campaignId),
      u_tk: this.adContext.userInstanceToken,
      u_id: this.adContext.userId,
      t_u: this.adContext.isTemporaryUser,
      u_os: this.adContext.deviceOs,
      dmn: this.adContext.domain,
      a_b: this.adContext.appBundle,
      pg: this.adContext.page,
      cr_id: this.adContext.creativeId,
      dsp: this.adContext.dsp,
      a_ex: this.adContext._appExchange
    };
  }

  sendCreativeTrack(eventType: CreativeEvent, event?: ClickEvent) {
    if (window.isOgyPreview) {
      return;
    }
    if (!this.formatConfig || !this.formatConfig.displayTrackers) {
      return;
    }

    const datas: CreativeTrack = {
      ...this.commonTrackData(),
      a_ex: this.adContext._appExchange,
      e: eventType,
      timer: this.uiTimerStore.viewableDuration || undefined
    };

    if (eventType === 'client_click' && event) {
      setClickPositionInTrack(datas, eventType, event);
    }

    DebugLog.log(`sendCreativeTrack: ${eventType}`, datas);

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.FORMAT_ANALYTICS_ENDPOINT,
          this.adContext.region
        ),
        'creative'
      ),
      datas
    );

    this.sendTrack(url);

    return url;
  }

  get impressionFormatSent() {
    return this.isImpressionFormatSent;
  }

  sendImpressionFormat() {
    if (this.shouldTrackFormatAvailability && this.isFormatAvailableInterval) {
      clearInterval(this.isFormatAvailableInterval);
    }

    if (window.isOgyPreview) {
      return;
    }

    /* https://jira.ogury.io/browse/SPY-15465
      The impressionFormatTrack feature-enabling is deactivate on external traffic
      This means, when MRAID_ENV.sdk = 'presage'
      For Equativ, Google Open Bidding, One Tag, this value can vary
      For now we for this impression format to not be sent for Beeswax only (the only case we had)
      before a specific revamp of this in both stack
     */
    if (this.adContext?.dsp === 'beeswax') {
      return;
    }

    const queryParams = {
      ...this.commonTrackData(),
      conn: window?.navigator?.connection?.type
        ? window.navigator.connection.type.toUpperCase()
        : 'WIFI',
      ad_ob: this.adContext.adObject,
      ...this.getRedirectType()
    };

    sendMraidDebugTracks(`format_send_impression_format`);
    DebugLog.log('sendImpressionFormat', queryParams);

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.FORMAT_ANALYTICS_ENDPOINT,
          this.adContext.region
        ),
        'impfmt'
      ),
      queryParams
    );

    this.sendTrack(url);
    this.isImpressionFormatSent = true;

    return url;
  }

  private getRedirectType(): { rt?: string } {
    const rt: string = this.formatConfig.redirectType;
    return rt ? { rt } : {};
  }

  sendAdsEventsTrack(event: string) {
    if (window.isOgyPreview) {
      return Promise.resolve();
    }
    const adTrackUrl = getOgAdContext().ad_track_url;
    if (!adTrackUrl) return Promise.resolve();

    const data = {
      apps_publishers: [this.adContext.apiKey],
      at: new Date().toISOString(),
      connectivity: window.navigator?.connection?.type || 'unknown',
      content: {
        event,
        advert: this.adContext.impressionId,
        advertiser: this.adContext.advertiserId,
        campaign: this.adContext.campaignId,
        ad_unit_id: this.adContext.adUnitId,
        pg: this.adContext.page,
        dmn: this.adContext.domain,
        dsp: this.adContext.dsp,
        a_ex: this.adContext._appExchange,
        creative_id: this.adContext.creativeId,
        t_u: this.adContext.isTemporaryUser
      },
      version: window.MRAID_ENV.sdkVersion
    };

    DebugLog.log(`sendTrack: ${event}`, data);

    this.pendingTracks++;

    return ajaxPromise
      .postJson(adTrackUrl, data, {
        headers: {
          User: this.adContext.userId,
          'Instance-Token': this.adContext.userInstanceToken,
          'Device-OS': this.adContext.deviceOs
        }
      })
      .finally(() => this.pendingTracks--);
  }

  sendSupplyMonitoringEvent(
    event: SUPPLY_MONITORING_EVENT_TYPE,
    additionalFields?: OptionalSupplyMonitoringEventFields
  ) {
    if (
      window.isOgyPreview ||
      !isSupplyMonitoringEventsEnabled(this.formatConfig)
    )
      return;

    const url = joinUrl(
      generateUrlWithRegion(
        globalConfig.MS_ADS_MONITORING_EVENTS_URL,
        this.adContext.region
      ),
      'supply-monitoring'
    );

    const screenSize = getScreenSize();
    const ogAdContext = getOgAdContext();

    const data = {
      environment_type: isWeb() ? 'web' : 'in-app',
      event,
      ad_session_id: ogAdContext.ad_session_id,
      asset_key: ogAdContext.ad_unit_config?.asset_key,
      media_type: ogAdContext.media_type,
      delivery_mode: ogAdContext.delivery_mode,
      bidding_provider: ogAdContext.bidding_provider,
      publisher_name: ogAdContext.publisher_name,
      advert_id: this.adContext.impressionId,
      ad_unit_id: this.adContext.adUnitId,
      ad_unit_type: this.adContext.adUnitType,
      campaign_id: `${this.adContext.campaignId}`,
      device_type: this.adContext.deviceType,
      location: this.adContext.page,
      tag_version:
        (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) ||
        this.adContext.sdkVersion ||
        'unknown',
      locale: navigator.language,
      device_screen_width: screenSize.width,
      device_screen_height: screenSize.height,
      sent_at: Math.floor(Date.now() / 1000),
      source: 'formats',
      device_os: this.adContext.deviceOs,
      ...additionalFields
      // TODO: a significant number of fields are missing here because they come from the bid_response object set in the adm, consider making it available in standalone context ?
    };

    if (
      this.adContext.adUnitType === 'in_article' &&
      this.adContext.inArticleType
    ) {
      (data as any).in_article_type = this.adContext.inArticleType;
    }

    DebugLog.log(`sendSupplyMonitoringEvent: ${event}`, data);

    this.pendingTracks++;

    return ajaxPromise.postJson(url, data).finally(() => this.pendingTracks--);
  }

  waitUntilTracksAreSent(): Promise<boolean> {
    if (this.trackWaitPromise) {
      return this.trackWaitPromise;
    }

    const waitPromise = new Promise<boolean>(resolve => {
      when(() => this.pendingTracks === 0).then(() => resolve(true));
    });

    const timeoutPromise = new Promise<boolean>(resolve =>
      setTimeout(() => resolve(false), 1000)
    );

    this.trackWaitPromise = Promise.race([waitPromise, timeoutPromise]);

    this.trackWaitPromise.finally(() => {
      this.trackWaitPromise = null;
    });

    return this.trackWaitPromise;
  }

  sendTrack(url: string): void {
    this.pendingTracks++;
    const pixel = new Image();
    const callback = action(() => this.pendingTracks--);
    pixel.onload = callback;
    pixel.onerror = callback;
    pixel.src = url;
  }

  sendJsTrack(url: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.srcdoc = `<script src="${url}"></script>`;
    document.body.appendChild(iframe);
  }
}
